/**
 * Location-aware brand carousel: classifies collections from product tags,
 * reorders express first, optional auto-scroll.
 *
 * Tag sources: Liquid outputs product.tags as JSON arrays. We also split comma-joined
 * strings (edge case) and align city_* with Skipp storage (e.g. Bengaluru vs city_bangalore).
 */
import { getUserLocation, slugId } from './skipp-user-location.js';

/**
 * Flatten one product's tags to lowercase tokens (Shopify array, comma string, or nested).
 * @param {unknown} raw
 * @returns {string[]}
 */
function productTagsToFlatList(raw) {
  const out = [];
  const visit = (x) => {
    if (x == null) return;
    if (Array.isArray(x)) {
      x.forEach(visit);
      return;
    }
    const s = String(x).trim();
    if (!s) return;
    if (s.includes(',')) {
      s.split(',').forEach((p) => visit(p.trim()));
      return;
    }
    out.push(s.toLowerCase());
  };
  visit(raw);
  return out;
}

/**
 * city_* tokens to match for the user's saved city (lowercase + slug + common aliases).
 * @param {string | null | undefined} userCity
 * @returns {string[]}
 */
function cityTagNeedles(userCity) {
  const city = String(userCity || '').trim().toLowerCase();
  if (!city) return [];

  const needles = new Set();
  const slug = slugId(userCity).replace(/-/g, '_');
  if (slug) needles.add(`city_${slug}`);

  const compact = city.replace(/[^a-z0-9]+/g, '');
  if (compact) needles.add(`city_${compact}`);

  if (
    city.includes('bengaluru') ||
    city.includes('bangalore') ||
    slug.includes('bengaluru') ||
    slug.includes('bangalore')
  ) {
    needles.add('city_bangalore');
    needles.add('city_bengaluru');
  }
  if (city.includes('mumbai') || city.includes('bombay') || slug.includes('mumbai') || slug.includes('bombay')) {
    needles.add('city_mumbai');
    needles.add('city_bombay');
  }

  return [...needles];
}

/**
 * @param {unknown[][]} productTagArrays — up to 15 products' tag rows from JSON
 * @param {string | null} city
 * @returns {boolean}
 */
export function brandCollectionIsExpress(productTagArrays, city) {
  if (!Array.isArray(productTagArrays) || productTagArrays.length === 0) return false;

  const needles = cityTagNeedles(city);
  const max = Math.min(productTagArrays.length, 15);

  for (let i = 0; i < max; i++) {
    const row = productTagArrays[i];
    const tags = productTagsToFlatList(row);
    if (!tags.includes('express')) continue;

    if (needles.length > 0) {
      for (let j = 0; j < needles.length; j++) {
        if (tags.includes(needles[j])) return true;
      }
    } else {
      /* No saved city: any product with `express` counts so badges still appear before location is set. */
      return true;
    }
  }
  return false;
}

function parseTagData(raw) {
  try {
    const data = JSON.parse(raw || '[]');
    if (!Array.isArray(data)) return [];
    return data.map((row) => {
      if (Array.isArray(row)) return row;
      if (typeof row === 'string') return row;
      if (row == null) return [];
      return [String(row)];
    });
  } catch {
    return [];
  }
}

const DEFAULT_LIGHTNING_ICON_URL =
  'https://cdn.shopify.com/s/files/1/0906/3288/0421/files/1_1f25fbc6-1922-4c9f-b3bb-014ca65b42b4.png?v=1777035003';

function createExpressBadgeContent(lightningIconUrl) {
  const frag = document.createDocumentFragment();
  const src = String(lightningIconUrl || '').trim() || DEFAULT_LIGHTNING_ICON_URL;
  const img = document.createElement('img');
  img.className = 'brand-badge__lightning';
  img.src = src;
  img.alt = '';
  img.loading = 'eager';
  img.decoding = 'async';
  frag.appendChild(img);
  const mins = document.createElement('span');
  mins.className = 'brand-badge__mins';
  mins.textContent = '30 mins';
  frag.appendChild(mins);
  return frag;
}

function reorderCards(track, cards, expressFirstIds) {
  const express = [];
  const standard = [];
  for (const card of cards) {
    const id = card.getAttribute('data-collection-id');
    if (expressFirstIds.has(id)) express.push(card);
    else standard.push(card);
  }
  const frag = document.createDocumentFragment();
  express.forEach((n) => frag.appendChild(n));
  standard.forEach((n) => frag.appendChild(n));
  track.appendChild(frag);
}

function updateBadges(cards, expressIds, showBadges, labelExpress, lightningIconUrl) {
  cards.forEach((card) => {
    const id = card.getAttribute('data-collection-id');
    const badge = card.querySelector('.brand-badge');
    if (!badge) return;
    if (!showBadges) {
      badge.hidden = true;
      badge.classList.remove('express');
      badge.textContent = '';
      badge.removeAttribute('aria-label');
      return;
    }
    const isExpress = expressIds.has(id);
    badge.classList.remove('express');
    if (isExpress) {
      badge.hidden = false;
      badge.classList.add('express');
      badge.textContent = '';
      badge.appendChild(createExpressBadgeContent(lightningIconUrl));
      badge.setAttribute('aria-label', labelExpress);
    } else {
      badge.hidden = true;
      badge.textContent = '';
      badge.removeAttribute('aria-label');
    }
  });
}

function initAutoScroll(scrollEl, speedStep, enabled) {
  if (!enabled || speedStep <= 0 || !scrollEl) return () => {};
  let raf = 0;
  let running = true;
  let paused = false;
  const speed = 0.15 + speedStep * 0.12;

  const tick = () => {
    if (!running) return;
    if (!paused && scrollEl.scrollWidth > scrollEl.clientWidth) {
      scrollEl.scrollLeft += speed;
      const max = scrollEl.scrollWidth - scrollEl.clientWidth;
      if (scrollEl.scrollLeft >= max - 1) {
        scrollEl.scrollLeft = 0;
      }
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  const pause = () => {
    paused = true;
  };
  const resume = () => {
    paused = false;
  };
  scrollEl.addEventListener('mouseenter', pause);
  scrollEl.addEventListener('mouseleave', resume);
  scrollEl.addEventListener('pointerenter', pause);
  scrollEl.addEventListener('pointerleave', resume);
  scrollEl.addEventListener('focusin', pause);
  scrollEl.addEventListener('focusout', resume);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    scrollEl.removeEventListener('mouseenter', pause);
    scrollEl.removeEventListener('mouseleave', resume);
    scrollEl.removeEventListener('pointerenter', pause);
    scrollEl.removeEventListener('pointerleave', resume);
    scrollEl.removeEventListener('focusin', pause);
    scrollEl.removeEventListener('focusout', resume);
  };
}

/**
 * @param {HTMLElement} root
 */
export function initBrandCarouselLocation(root) {
  if (!root) return;
  const track = root.querySelector('[data-brand-track]');
  if (!track) return;
  const scrollEl = root.querySelector('[data-brand-scroll]') || track;

  const cards = [...track.querySelectorAll('[data-brand-card]')];
  if (!cards.length) return;

  const showBadges = root.getAttribute('data-show-badges') !== 'false';
  const autoScroll = root.getAttribute('data-auto-scroll') === 'true';
  const scrollSpeed = Math.max(1, Math.min(10, parseInt(root.getAttribute('data-scroll-speed') || '3', 10) || 3));
  const labelExpress = root.getAttribute('data-label-express') || '30 minute delivery';
  const lightningIconUrl =
    root.getAttribute('data-lightning-icon-url') || DEFAULT_LIGHTNING_ICON_URL;

  const loc = getUserLocation();
  const city = loc?.city != null ? String(loc.city).trim().toLowerCase() : null;

  const expressIds = new Set();
  for (const card of cards) {
    const tagsPayload = parseTagData(card.getAttribute('data-product-tags'));
    const title = card.getAttribute('data-collection-title') || card.getAttribute('data-collection-id');
    const isExpress = brandCollectionIsExpress(tagsPayload, city);
    console.log('[Skipp brand-carousel]', title, isExpress);
    if (isExpress) expressIds.add(card.getAttribute('data-collection-id'));
  }

  reorderCards(track, cards, expressIds);
  updateBadges(
    [...track.querySelectorAll('[data-brand-card]')],
    expressIds,
    showBadges,
    labelExpress,
    lightningIconUrl
  );

  if (!showBadges) {
    root.classList.add('brand-carousel-location--no-badges');
  }

  initAutoScroll(scrollEl, scrollSpeed, autoScroll);

  document.addEventListener(
    'skipp:user-location-changed',
    () => {
      const cardsNow = [...track.querySelectorAll('[data-brand-card]')];
      const loc2 = getUserLocation();
      const city2 = loc2?.city != null ? String(loc2.city).trim().toLowerCase() : null;
      const nextExpress = new Set();
      for (const card of cardsNow) {
        const tagsPayload = parseTagData(card.getAttribute('data-product-tags'));
        const title = card.getAttribute('data-collection-title') || card.getAttribute('data-collection-id');
        const isExpress = brandCollectionIsExpress(tagsPayload, city2);
        console.log('[Skipp brand-carousel]', title, isExpress);
        if (isExpress) nextExpress.add(card.getAttribute('data-collection-id'));
      }
      reorderCards(track, cardsNow, nextExpress);
      updateBadges(
        [...track.querySelectorAll('[data-brand-card]')],
        nextExpress,
        showBadges,
        labelExpress,
        lightningIconUrl
      );
    },
    { passive: true }
  );
}

function boot() {
  document.querySelectorAll('[data-brand-carousel-location]').forEach((root) => initBrandCarouselLocation(root));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
